import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { parseCurrency } from 'utils/parsers';
import {
  ChartCard,
  ChartEmpty,
  ChartGrid,
  ChartSubtitle,
  ChartTitle,
} from './styled';

// Graficos do painel de desempenho dos operadores (screens/Billing/
// RelatorioDesempenho), com o @mui/x-charts. Recebem os dados prontos do
// buildOperatorPerformance (utils/billingReports.js) e nao calculam nada.

export { ChartGrid };

const BLUE = '#2670D1';
const GREY = '#cbd5e0';
const PURPLE = '#7B44F2';
const GREEN = '#00D32A';
const RED = '#dd4952';
const ORANGE = '#FF9500';

const money = (value) => (Number.isFinite(value) ? parseCurrency(value) : '');

const Card = ({ title, subtitle, wide, empty, height = 240, children }) => (
  <ChartCard $wide={wide}>
    <ChartTitle>{title}</ChartTitle>
    {!subtitle ? null : <ChartSubtitle>{subtitle}</ChartSubtitle>}
    {empty ? (
      <ChartEmpty $height={height}>Sem dados no período</ChartEmpty>
    ) : (
      children
    )}
  </ChartCard>
);

// Altura das barras horizontais conforme o numero de operadores.
const rankingHeight = (count) => Math.max(200, 60 + count * 34);

// Tentativas por dia, empilhadas entre contato efetivo e sem contato.
export const AttemptsByDayChart = ({ daily }) => {
  const empty = !(daily || []).some((day) => day.effective || day.notEffective);
  return (
    <Card
      title="Tentativas por dia"
      subtitle="Contato efetivo: houve conversa com o cliente"
      wide
      empty={empty}
    >
      <BarChart
        height={260}
        dataset={daily}
        xAxis={[{ scaleType: 'band', dataKey: 'label' }]}
        series={[
          {
            dataKey: 'effective',
            label: 'Contato efetivo',
            stack: 'total',
            color: BLUE,
          },
          {
            dataKey: 'notEffective',
            label: 'Sem contato',
            stack: 'total',
            color: GREY,
          },
        ]}
        margin={{ top: 40, right: 16, bottom: 30, left: 40 }}
      />
    </Card>
  );
};

// Distribuicao dos resultados das tentativas.
export const ResultsChart = ({ results }) => (
  <Card title="Resultado das tentativas" empty={!results?.length}>
    <PieChart
      height={240}
      series={[
        {
          data: results || [],
          innerRadius: 50,
          paddingAngle: 1,
          cornerRadius: 3,
        },
      ]}
      margin={{ top: 10, right: 200, bottom: 10, left: 10 }}
      slotProps={{
        legend: {
          direction: 'column',
          position: { vertical: 'middle', horizontal: 'right' },
          itemMarkWidth: 10,
          itemMarkHeight: 10,
          labelStyle: { fontSize: 12 },
        },
      }}
    />
  </Card>
);

// Promessas por operador: cumpridas, quebradas e ainda no prazo.
export const PromisesChart = ({ rows }) => {
  const data = (rows || []).filter((row) => row.promises);
  return (
    <Card
      title="Promessas de pagamento"
      subtitle="Cumprida: título pago até 3 dias depois da data prometida"
      empty={!data.length}
      height={rankingHeight(data.length)}
    >
      <BarChart
        layout="horizontal"
        height={rankingHeight(data.length)}
        dataset={data}
        yAxis={[{ scaleType: 'band', dataKey: 'axisLabel' }]}
        series={[
          {
            dataKey: 'promisesKept',
            label: 'Cumpridas',
            stack: 'promises',
            color: GREEN,
          },
          {
            dataKey: 'promisesBroken',
            label: 'Quebradas',
            stack: 'promises',
            color: RED,
          },
          {
            dataKey: 'promisesPending',
            label: 'No prazo',
            stack: 'promises',
            color: GREY,
          },
        ]}
        margin={{ top: 40, right: 16, bottom: 30, left: 130 }}
      />
    </Card>
  );
};

// Ranking de atendimento: tentativas e contatos efetivos por operador.
export const AttemptsRankingChart = ({ rows }) => {
  const data = (rows || []).filter((row) => row.attempts);
  return (
    <Card
      title="Atendimento por operador"
      empty={!data.length}
      height={rankingHeight(data.length)}
    >
      <BarChart
        layout="horizontal"
        height={rankingHeight(data.length)}
        dataset={data}
        yAxis={[{ scaleType: 'band', dataKey: 'axisLabel' }]}
        series={[
          { dataKey: 'attempts', label: 'Tentativas', color: GREY },
          { dataKey: 'effective', label: 'Contato efetivo', color: BLUE },
        ]}
        margin={{ top: 40, right: 16, bottom: 30, left: 130 }}
      />
    </Card>
  );
};

// Resultado financeiro por operador: renegociado e baixado na tela.
export const FinancialChart = ({ rows }) => {
  const data = (rows || []).filter(
    (row) => row.renegotiatedVolume || row.settledVolume
  );
  return (
    <Card
      title="Resultado financeiro por operador"
      subtitle="Valor renegociado e valor das baixas manuais"
      empty={!data.length}
      height={rankingHeight(data.length)}
    >
      <BarChart
        layout="horizontal"
        height={rankingHeight(data.length)}
        dataset={data}
        yAxis={[{ scaleType: 'band', dataKey: 'axisLabel' }]}
        xAxis={[{ valueFormatter: (value) => money(value) }]}
        series={[
          {
            dataKey: 'renegotiatedVolume',
            label: 'Renegociado',
            color: PURPLE,
            valueFormatter: money,
          },
          {
            dataKey: 'settledVolume',
            label: 'Baixado',
            color: ORANGE,
            valueFormatter: money,
          },
        ]}
        margin={{ top: 40, right: 24, bottom: 30, left: 130 }}
      />
    </Card>
  );
};
